import { useRef, useState, type ChangeEvent } from 'react'
import { accepts, pickerNote } from '../lib/filePicker'
import { Icon } from './icons'
import { useStaffText } from './StaffText'
import '../styles/studio.css'

// A file input that looks like the rest of the product.
//
// The browser's own <input type="file"> draws a button and a sentence in the
// browser's language, not the reader's, and in neither theme's colours. So the
// input is kept, hidden, for its dialog, and a design-system button opens it.
// The hidden input is out of the tab order and hidden from assistive technology,
// because the button is the one control a person reaches.

export function FilePicker(props: {
  accept?: string
  disabled?: boolean
  /** The button's words. Defaults to say('common.chooseFile'). */
  label?: string
  /** Called with the chosen file. The input is emptied afterwards, so the same file can be chosen again. */
  onFile?: (file: File) => void
  /** Several files may be chosen; all of them, accepted or not, are handed here and the caller says which it refused. */
  onFiles?: (files: File[]) => void
  id?: string
  'aria-describedby'?: string
  'aria-label'?: string
}) {
  const { say } = useStaffText()
  const inputRef = useRef<HTMLInputElement>(null)
  const [lastName, setLastName] = useState<string | undefined>(undefined)
  const [refused, setRefused] = useState<string | undefined>(undefined)

  function changed(e: ChangeEvent<HTMLInputElement>) {
    if (props.onFiles) {
      const files = [...(e.target.files ?? [])]
      e.target.value = ''
      if (files.length > 0) props.onFiles(files)
      return
    }
    const file = e.target.files?.[0]
    // Emptied whatever happens, so that choosing the same file again is a change.
    e.target.value = ''
    if (!file) return
    // The dialog's "All files" ignores the filter. A file we did not ask for is
    // left unchosen here, and the line beside the button says so, since a click
    // that does nothing reads as a broken button; the server's own check still
    // stands behind this one.
    if (!accepts(props.accept, file)) {
      setRefused(file.name)
      return
    }
    setRefused(undefined)
    setLastName(file.name)
    props.onFile?.(file)
  }

  const note = pickerNote(lastName, refused, {
    noneChosen: say('common.noFileChosen'),
    notAccepted: (name) => say('common.fileNotAccepted', { name }),
  })

  return (
    <span className="studio-file">
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        accept={props.accept}
        multiple={Boolean(props.onFiles)}
        disabled={props.disabled}
        onChange={changed}
      />
      <button
        type="button"
        className="btn"
        id={props.id}
        disabled={props.disabled}
        aria-describedby={props['aria-describedby']}
        aria-label={props['aria-label']}
        onClick={() => inputRef.current?.click()}
      >
        <Icon name="upload" size={16} />
        {props.label ?? say('common.chooseFile')}
      </button>
      {/* A status region that stays mounted, so a refusal is read out. Several files are said by their caller. */}
      {!props.onFiles && (
        <span className="studio-file__name" data-refused={note.refused || undefined} role="status">
          {note.text}
        </span>
      )}
    </span>
  )
}
